import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { SyncStateData, MatrixClient, MatrixEvent, Room, SyncState } from 'matrix-js-sdk';
import { useAuth } from '@/stores/authContext';
import { tokenStore } from '@/auth/tokenStore';
import { refreshAccessToken } from '@/auth/refreshManager';
import { decodeAccessToken } from '@/auth/tokenClaims';
import { config } from '@/config';
import {
  cacheCreds,
  clearCreds,
  getCachedCreds,
  isExpectedEmptyChat,
  matrixLogin,
  MatrixLoginError,
  type MatrixCreds,
} from '@/chat/matrixSession';
import { ChatContext, type ChatContextValue } from '@/chat/chatContext';
import { computeWritable, isDirectRoom } from '@/chat/roomDescriptor';
import { isOpenChatVisible, type ActiveChatLocation } from '@/chat/chatSoundVisibility';
import { playBellSound, playChatSound } from '@/lib/notificationSound';
import type { ChatConnectionStatus, ChatRoomSummary } from '@/types/chat';

/**
 * Есть ли непрочитанное: последнее видимое сообщение НЕ от меня и я его ещё не прочитал (по read-receipt).
 * Намеренно НЕ используем getUnreadNotificationCount — это push-счётчик, у части аккаунтов/комнат он 0
 * даже при реально непрочитанных сообщениях (push-правила). Read-receipt — надёжный источник.
 */
function roomHasUnread(room: Room, myUserId: string): boolean {
  const events = room.getLiveTimeline().getEvents();
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const e = events[i];
    if (!e || e.getType() !== 'm.room.message' || e.isRedacted()) {
      continue;
    }
    // События-правки (m.replace) — не отдельные сообщения, пропускаем.
    const rel = (e.getContent() as Record<string, unknown>)['m.relates_to'] as { rel_type?: string } | undefined;
    if (rel?.rel_type === 'm.replace') {
      continue;
    }
    if (e.getSender() === myUserId) {
      return false; // последнее сообщение — моё → читать нечего
    }
    const id = e.getId();
    return id != null && !room.hasUserReadEvent(myUserId, id);
  }
  return false;
}

/** Сводка комнат пользователя (только joined) + число комнат с непрочитанным (для точек-индикаторов). */
function summarizeRooms(client: MatrixClient): { rooms: ChatRoomSummary[]; unread: number } {
  const myUserId = client.getUserId() ?? '';
  const rooms: ChatRoomSummary[] = [];
  let unread = 0;
  for (const room of client.getRooms()) {
    if (room.getMyMembership() !== 'join') {
      continue;
    }
    // Общение ведётся только через агента: комнаты без агента (прямые по сделке и отклики
    // на предложения) не показываем и в непрочитанном не учитываем. Правило безусловное —
    // прежний фиче-флаг снят вместе с самой возможностью прямого общения.
    if (isDirectRoom(room)) {
      continue;
    }
    const isUnread = roomHasUnread(room, myUserId);
    if (isUnread) {
      unread += 1;
    }
    // Активный чат vs read-only (завершённая сделка) — по power-level права на отправку.
    const power = room.currentState.getStateEvents('m.room.power_levels', '')?.getContent();
    const writable = computeWritable(power as Parameters<typeof computeWritable>[0], myUserId);
    rooms.push({
      roomId: room.roomId,
      name: room.name || room.roomId,
      unreadCount: isUnread ? 1 : 0,
      writable,
      lastActivityTs: room.getLastActiveTimestamp(),
    });
  }
  rooms.sort((a, b) => b.unreadCount - a.unreadCount || b.lastActivityTs - a.lastActivityTs);
  return { rooms, unread };
}

/**
 * Актуален ли app-токен по claim `exp` (с запасом 30 c). Нет exp — считаем валидным (зря не рефрешим).
 * Matrix-логин идёт напрямую (fetch), мимо авто-refresh транспорта, поэтому свежесть токена перед
 * логином обеспечиваем сами.
 */
function isAccessTokenExpired(token: string): boolean {
  const exp = decodeAccessToken(token)?.exp;
  return exp != null && exp * 1000 <= Date.now() + 30_000;
}

/**
 * Логин в Matrix ровно ОДНИМ запросом к homeserver. App-токен перед логином обновляем, только если он
 * истёк — тогда 403 M_FORBIDDEN от Synapse однозначно значит «нет Matrix-аккаунта» (провижнится при
 * первой сделке), а не протухший токен. Повторный логин на 403 намеренно НЕ делаем: у нового пользователя
 * логин всегда 403, и удвоение запросов упирало бы в rate-limit homeserver (rc_login → 429). Если refresh
 * не удался — сессия истекла (refreshManager уже инициировал выход), пробрасываем ошибку.
 */
async function loginMatrix(username: string): Promise<MatrixCreds> {
  let token = tokenStore.getAccess();
  if (!token || isAccessTokenExpired(token)) {
    token = await refreshAccessToken();
    if (!token) {
      throw new MatrixLoginError('session expired');
    }
  }
  return matrixLogin(token, username);
}

// Жизненный цикл Matrix-клиента для всего аутентифицированного приложения (монтируется в AppLayout).
// matrix-js-sdk импортируется ДИНАМИЧЕСКИ → отдельный чанк (не в initial-бандле). Крипто не включаем.
export default function ChatProvider({ children }: { readonly children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState<ChatConnectionStatus>('idle');
  const [rooms, setRooms] = useState<readonly ChatRoomSummary[]>([]);
  const [unreadTotal, setUnreadTotal] = useState(0);

  const clientRef = useRef<MatrixClient | null>(null);
  const [, forceClientTick] = useState(0);
  // Поколение подключения — отбрасываем результаты устаревших попыток (StrictMode/перелогин/гонки).
  const genRef = useRef(0);
  // Счётчик перелогинов Matrix — переживает re-entry connect(), чтобы НЕ зациклиться при упорном отказе сервера.
  const reloginRef = useRef(0);
  // Завершён ли первичный sync — до него события таймлайна приходят как история, звук на них не нужен.
  const syncReadyRef = useRef(false);
  // Известные комнаты (roomId), чтобы озвучить ПОЯВЛЕНИЕ новой (напр. началась сделка → чат создан
  // упреждающе). null = базовый набор ещё не зафиксирован (фиксируем при готовности sync — без звука,
  // чтобы уже существующие комнаты не пищали при загрузке/переподключении).
  const knownRoomIdsRef = useRef<Set<string> | null>(null);
  // Текущий маршрут и открытая комната (`?room=`) — читается из ref в обработчике таймлайна (без пере-
  // подключения): звук нового сообщения глушим только если этот чат сейчас виден на экране.
  const activeChatRef = useRef<ActiveChatLocation>({ pathname: location.pathname, roomParam: null });

  const teardown = useCallback(() => {
    const client = clientRef.current;
    if (client) {
      client.removeAllListeners();
      client.stopClient();
      clientRef.current = null;
    }
    setRooms([]);
    setUnreadTotal(0);
  }, []);

  // Держим ref с текущим маршрутом и открытой комнатой в актуальном состоянии (обработчик таймлайна
  // читает именно ref, т.к. живёт в замыкании connect() и обычные пропсы/состояние там устарели бы).
  useEffect(() => {
    activeChatRef.current = { pathname: location.pathname, roomParam: new URLSearchParams(location.search).get('room') };
  }, [location]);

  const connect = useCallback(async (isRelogin = false) => {
    const gen = ++genRef.current;
    teardown();
    syncReadyRef.current = false; // новое подключение — пока не готово, звук на событиях таймлайна молчит
    knownRoomIdsRef.current = null; // и базовый набор комнат зафиксируем заново (без звука на переподключении)
    if (!isRelogin) {
      reloginRef.current = 0; // пользовательское подключение (mount/reconnect) — сбрасываем лимит перелогинов
    }
    setStatus('connecting');

    const appToken = tokenStore.getAccess();
    const claims = decodeAccessToken(appToken);
    if (!appToken || !claims) {
      if (gen === genRef.current) {
        setStatus('error');
      }
      return;
    }
    const username = claims.preferredUsername;

    try {
      const sdk = await import('matrix-js-sdk');
      if (gen !== genRef.current) {
        return;
      }

      const obtainCreds = async (forceLogin: boolean): Promise<MatrixCreds> => {
        if (!forceLogin) {
          const cached = getCachedCreds(username);
          if (cached) {
            return cached;
          }
        }
        const fresh = await loginMatrix(username);
        cacheCreds(username, fresh);
        return fresh;
      };

      const creds = await obtainCreds(false);
      if (gen !== genRef.current) {
        return;
      }

      const client = sdk.createClient({
        baseUrl: config.matrixServerUrl,
        accessToken: creds.accessToken,
        userId: creds.userId,
        deviceId: creds.deviceId,
      });
      clientRef.current = client;
      forceClientTick((n) => n + 1);

      const refresh = (): void => {
        if (gen !== genRef.current) {
          return;
        }
        const snap = summarizeRooms(client);
        setRooms(snap.rooms);
        setUnreadTotal(snap.unread);
        // Звук на ПОЯВЛЕНИЕ новой комнаты (началась сделка → чат создан упреждающе, Matrix /sync добавил
        // комнату). Только после готовности sync; первый пересчёт лишь фиксирует базовый набор (без звука),
        // чтобы существующие комнаты не пищали. Колокольчик-уведомление (не звук сообщения); общий throttle
        // 'bell' схлопывает со звуком уведомления «новая сделка», если оно пришло рядом. Движок уважает
        // переключатель звука.
        if (syncReadyRef.current) {
          const currentIds = new Set(snap.rooms.map((room) => room.roomId));
          const known = knownRoomIdsRef.current;
          if (known !== null && snap.rooms.some((room) => !known.has(room.roomId))) {
            playBellSound();
          }
          knownRoomIdsRef.current = currentIds;
        }
      };

      // Протухание Matrix-токена → сбросить кэш и перелогиниться (с лимитом, чтобы не зациклиться).
      const handleTokenExpired = async (): Promise<void> => {
        if (gen !== genRef.current) {
          return;
        }
        if (reloginRef.current >= 2) {
          setStatus('error'); // сервер упорно отвергает — прекращаем, чтобы не штормить login’ами
          return;
        }
        reloginRef.current += 1;
        clearCreds(username);
        try {
          const renewed = await loginMatrix(username);
          cacheCreds(username, renewed);
          if (gen === genRef.current) {
            connect(true); // реконнект с новыми кредами (relogin — лимит не сбрасываем)
          }
        } catch (error) {
          if (gen === genRef.current) {
            // teardown() держит инвариант «empty/error ⇒ клиента нет»: старый клиент после отказа
            // перелогина не должен остаться живым (иначе пустой экран поверх фонового sync).
            teardown();
            setStatus(isExpectedEmptyChat(error) ? 'empty' : 'error');
          }
        }
      };

      client.on(sdk.HttpApiEvent.SessionLoggedOut, () => void handleTokenExpired());
      client.on(sdk.ClientEvent.Sync, (state: SyncState, _prev: SyncState | null, data?: SyncStateData) => {
        if (gen !== genRef.current) {
          return;
        }
        if (state === sdk.SyncState.Prepared) {
          setStatus('ready');
          reloginRef.current = 0; // успешный sync — сбрасываем лимит перелогинов для будущих протуханий
          syncReadyRef.current = true; // с этого момента новые сообщения таймлайна — живые (звучат)
          refresh();
        } else if (state === sdk.SyncState.Syncing) {
          setStatus((prev) => (prev === 'ready' ? prev : 'syncing'));
        } else if (state === sdk.SyncState.Error) {
          const errcode = (data?.error as { errcode?: string } | undefined)?.errcode;
          if (errcode === 'M_UNKNOWN_TOKEN') {
            void handleTokenExpired();
          } else {
            setStatus('error');
          }
        }
      });
      client.on(sdk.RoomEvent.MyMembership, (room: Room, membership: string) => {
        if (gen !== genRef.current) {
          return; // устаревшее поколение — не трогаем join/state после teardown
        }
        if (membership === sdk.KnownMembership.Invite) {
          client.joinRoom(room.roomId).catch(() => {
            /* приглашение могло устареть — игнорируем, sync подровняет список */
          });
        }
        refresh();
      });
      client.on(
        sdk.RoomEvent.Timeline,
        (event: MatrixEvent, room: Room | undefined, toStartOfTimeline?: boolean, _removed?: boolean, data?: { liveEvent?: boolean }) => {
          if (gen !== genRef.current) {
            return;
          }
          refresh();
          // Звук — только на настоящее новое входящее: живое событие (не догон истории и не первичный
          // sync), текстовое сообщение, чужой отправитель, в показываемой (агентской) комнате. Правки
          // (m.replace) — не новые сообщения, пропускаем. Движок ограничивает частоту и уважает переключатель.
          if (
            !syncReadyRef.current ||
            data?.liveEvent !== true ||
            toStartOfTimeline === true ||
            event.getType() !== 'm.room.message' ||
            event.getSender() === creds.userId ||
            room == null ||
            isDirectRoom(room)
          ) {
            return;
          }
          const relatesTo = (event.getContent() as Record<string, unknown>)['m.relates_to'] as { rel_type?: string } | undefined;
          if (relatesTo?.rel_type === 'm.replace') {
            return;
          }
          // Не озвучиваем сообщение, пришедшее в открытый и видимый на экране чат — пользователь его и так
          // видит. Звук нужен для других комнат раздела «Мои чаты» и когда чата на экране нет (другой
          // раздел или вкладка в фоне).
          if (isOpenChatVisible(activeChatRef.current, room.roomId)) {
            return;
          }
          playChatSound();
        },
      );
      // Read-receipt (в т.ч. свой, после markRead) сбрасывает счётчик непрочитанного — пересчитываем
      // сводку, чтобы зелёные точки (в списке и на «Мои чаты») гасли через ~секунду после открытия чата.
      client.on(sdk.RoomEvent.Receipt, () => refresh());

      await client.startClient({ initialSyncLimit: 30 });
    } catch (error) {
      if (gen === genRef.current) {
        // teardown() держит инвариант «empty/error ⇒ клиента нет» (startClient мог частично подняться).
        teardown();
        // 403 M_FORBIDDEN (нет аккаунта) или 429 M_LIMIT_EXCEEDED (rate-limit логина у пользователя без
        // кэш-кредов, т.е. без аккаунта) = чатов ещё нет → пустое состояние, а не ошибка связи.
        setStatus(isExpectedEmptyChat(error) ? 'empty' : 'error');
      }
    }
  }, [teardown]);

  useEffect(() => {
    if (!isAuthenticated) {
      genRef.current += 1; // инвалидируем любые незавершённые попытки
      teardown();
      setStatus('idle');
      return;
    }
    void connect();
    return () => {
      genRef.current += 1;
      teardown();
    };
  }, [isAuthenticated, connect, teardown]);

  const value: ChatContextValue = {
    status,
    rooms,
    unreadTotal,
    client: clientRef.current,
    reconnect: () => void connect(),
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
